import { useMemo, useState } from 'react';
import {
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGrid2Plus,
  faMagnifyingGlass,
} from '@fortawesome/pro-regular-svg-icons';
import { isConnectionBroken, type ConnectableProvider } from '@cowbell/moo';
import {
  useConnectableProviders,
  useDeleteUserConnection,
  useStartConnectionAuthorize,
} from '../../hooks/useUserConnections';
import { ProviderIcon } from '../../components/ProviderIcon';
import { describeProvider } from '../../components/provider-descriptions';
import { studioPortalProps } from '../../components/studioPortal';
import {
  StudioEntityCard,
  toProviderEntity,
} from '../../../../pages/moo/studio-components';
import { PaneHeader } from './PaneHeader';
import styles from './AppsPane.module.css';
import type_ from './settingsType.module.css';

/**
 * OMNI-828 Task 7: this is the real "connected apps personal cards" markup
 * the plan meant to migrate — `MooStudioConnectedAppsView.tsx` (the file the
 * brief names) is a 19-line wrapper around this same `AppsPane`/`AppCard`,
 * shared with the OMNI Settings modal (`MooSettingsModal.tsx`). There is
 * nothing to migrate in the wrapper itself; migrating here is what actually
 * changes both the standalone Moo Studio page AND the chat-widget settings
 * modal, since they render the identical component. See task-7-report.md.
 */
function AppCard({ provider }: { readonly provider: ConnectableProvider }) {
  const { mutate: startAuthorize, isLoading: isConnecting } =
    useStartConnectionAuthorize();
  const { mutate: disconnect, isLoading: isDisconnecting } =
    useDeleteUserConnection();

  const [confirmOpen, setConfirmOpen] = useState(false);

  const broken = isConnectionBroken(provider);

  // OMNI-828: `status` is what used to be a same-weight "Connected" button
  // (Task 7 brief: "resolves the audit's 'Connected vs Connect same weight'
  // note"). A broken grant gets its own status too, rather than only the
  // healthy case — "Reconnect needed" was plain orange body text before,
  // and StudioEntityCard's description Text has no per-state color (this
  // task doesn't touch that shared component), so the badge is where that
  // message can still render distinctly from the account-label text.
  const status = (() => {
    if (!provider.connected) return undefined;
    if (broken) {
      return { kind: 'attention' as const, label: 'Reconnect needed' };
    }
    return { kind: 'active' as const, label: 'Connected' };
  })();

  // A broken grant is still "connected", but only re-running OAuth repairs it.
  // Reconnect leads; Disconnect is still offered, because a user whose app was
  // revoked or removed by an admin can never complete OAuth again and would
  // otherwise have no way to clear the dead grant. Unchanged from before this
  // migration — no aria-label needed, its own button text already reads
  // "Disconnect" plainly.
  const brokenDisconnectButton = (
    <Button
      variant="subtle"
      color="red"
      size="sm"
      loading={isDisconnecting}
      onClick={() => setConfirmOpen(true)}
    >
      Disconnect
    </Button>
  );

  // OMNI-828 (Task 7 Step 3 — flagged deviation, see task-7-report.md): the
  // brief's literal "put NOTHING in actions" for the connected state would
  // remove the only way to disconnect a healthy personal connection from
  // this card, breaking real (tested) functionality. A quiet, low-emphasis
  // action alongside the new status badge satisfies the audit's actual
  // complaint — Connected now reads as a genuine badge, not a re-labeled
  // button carrying Connect's own visual weight — without deleting a working
  // capability. `aria-label` is still needed: this button's own text is
  // always "Disconnect" now (no more hover-swap), so screen-reader users
  // tabbing across several cards need the provider name to tell them apart.
  const quietDisconnectButton = (
    <Button
      variant="subtle"
      color="gray"
      size="compact-sm"
      loading={isDisconnecting}
      onClick={() => setConfirmOpen(true)}
      aria-label={`Disconnect ${provider.displayName}`}
    >
      Disconnect
    </Button>
  );

  const actions = (() => {
    if (broken) {
      return (
        <Group gap="xs">
          <Button
            size="sm"
            loading={isConnecting}
            onClick={() => startAuthorize(provider.provider)}
          >
            Reconnect
          </Button>
          {brokenDisconnectButton}
        </Group>
      );
    }
    if (provider.connected) {
      return quietDisconnectButton;
    }
    return (
      <Button
        variant="default"
        size="sm"
        loading={isConnecting}
        onClick={() => startAuthorize(provider.provider)}
      >
        Connect
      </Button>
    );
  })();

  const entity = {
    ...toProviderEntity({
      id: provider.provider,
      name: provider.displayName,
      subtitle: describeProvider(provider.provider),
      // Only the healthy-connected case shows the account label — mirrors
      // the exact mutual exclusivity the previous inline markup had (broken
      // showed "Reconnect needed" text instead of the account label; that
      // message now lives on the status badge rather than duplicating it
      // here too).
      description:
        provider.connected && provider.externalAccountLabel && !broken
          ? provider.externalAccountLabel
          : undefined,
      status,
    }),
    icon: (
      <ProviderIcon
        provider={provider.provider}
        logoUrl={provider.logoUrl}
        displayName={provider.displayName}
      />
    ),
  };

  return (
    <>
      <StudioEntityCard variant="card" entity={entity} actions={actions} />

      <Modal
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={`Disconnect ${provider.displayName}?`}
        size="sm"
        centered
        // This pane renders inside the composer settings modal, which itself
        // sits above the AskMoo widget at 1400.
        zIndex={1500}
        styles={{ inner: { left: 0, right: 0 } }}
        {...studioPortalProps()}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Moo will lose access to your {provider.displayName} account until
            you connect it again. This does not affect anyone else.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={isDisconnecting}
              onClick={() => {
                disconnect(provider.provider);
                setConfirmOpen(false);
              }}
            >
              Disconnect
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

interface AppsPaneProps {
  /**
   * Defaults to "Connect apps" for the OMNI Settings modal. The standalone
   * Moo Studio page (`MooStudioConnectedAppsView`) overrides this to "My
   * Apps" so the page heading agrees with its own sidebar nav label
   * (OMNI-824) — the two surfaces are allowed to name this pane differently.
   */
  readonly title?: string;
}

/** Connect apps — the user's own OAuth connections to admin-approved apps. */
export function AppsPane({ title = 'Connect apps' }: AppsPaneProps) {
  const { data: providers, isLoading, isError } = useConnectableProviders();
  const [search, setSearch] = useState('');

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return providers ?? [];
    return (providers ?? []).filter((p) =>
      p.displayName.toLowerCase().includes(term)
    );
  }, [providers, search]);

  return (
    <div className={styles.pane}>
      <PaneHeader
        icon={faGrid2Plus}
        title={title}
        subtitle="Connect admin-approved apps so Moo can use them on your behalf. These connections are yours alone."
      />

      {isLoading && (
        <div className={styles.centered} data-testid="apps-pane-loading">
          <Loader size="md" />
        </div>
      )}

      {isError && (
        <Paper withBorder className={styles.centered}>
          <Text className={type_.subtitle}>
            We couldn&apos;t load your apps. Refresh to try again.
          </Text>
        </Paper>
      )}

      {!isLoading && !isError && (
        <>
          <TextInput
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder="Search apps"
            aria-label="Search apps"
            leftSection={<FontAwesomeIcon icon={faMagnifyingGlass} />}
            className={styles.search}
          />

          {visible.length > 0 && (
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              {visible.map((provider) => (
                <AppCard key={provider.provider} provider={provider} />
              ))}
            </SimpleGrid>
          )}

          {visible.length === 0 && (providers?.length ?? 0) > 0 && (
            <Paper withBorder className={styles.centered}>
              <Text className={type_.subtitle}>
                No apps match “{search.trim()}”.
              </Text>
            </Paper>
          )}

          {(providers?.length ?? 0) === 0 && (
            <Paper withBorder className={styles.centered}>
              <Stack gap="xs" align="center">
                <Text className={type_.cardTitle}>No apps available yet</Text>
                <Text className={type_.subtitle} ta="center">
                  Your administrator decides which apps can be connected. Once
                  they enable one, it will show up here.
                </Text>
              </Stack>
            </Paper>
          )}
        </>
      )}
    </div>
  );
}
